import './styles/global.css'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, useSyncExternalStore } from 'react'
import { Dashboard } from './pages/Dashboard'
import { PostEditor } from './pages/PostEditor'
import { ShareView } from './pages/ShareView'
import { ShareRedeemView } from './pages/ShareRedeemView'
import { EmbedView } from './pages/EmbedView'
import { Login } from './pages/Login'
import { Signup } from './pages/Signup'
import { VerifyEmailView } from './pages/VerifyEmailView'
import { AuthCallback } from './pages/AuthCallback'
import { isLoggedIn, subscribe, refreshAccessToken } from './auth'

function useAuth() {
  return useSyncExternalStore(subscribe, isLoggedIn)
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const authed = useAuth()
  const location = useLocation()
  if (!authed) {
    // Ziel merken, damit die Login-Seite nach Erfolg dorthin zurückführt.
    const next = `${location.pathname}${location.search}`
    const to = next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'
    return <Navigate to={to} replace />
  }
  return <>{children}</>
}

// Token refresh strategy — auth-api issues access-tokens with 15min TTL.
//
//   1. Immediate refresh on mount: if the page is loaded with a token
//      that's already past its TTL (very common — user closes tab and
//      reopens hours later, or switches browsers and the new browser's
//      localStorage has a stale snapshot), we MUST refresh before the
//      first WS connect happens, otherwise the connect 403s and the
//      Yjs provider sits in an endless retry loop with a dead token.
//
//   2. Periodic refresh every 10 min keeps long-running tabs alive.
//
//   3. REST calls have their own 401-retry fallback in api/client.ts.
function useTokenRefreshLoop() {
  useEffect(() => {
    if (!isLoggedIn()) return
    // Kick off an immediate refresh — fire and forget.
    void refreshAccessToken()
    const id = window.setInterval(() => {
      void refreshAccessToken()
    }, 10 * 60 * 1000)
    return () => window.clearInterval(id)
  }, [])
}

export default function App() {
  useTokenRefreshLoop()
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmailView />} />
      {/* AuthAPI's mail uses path-based /verify/<token> URLs */}
      <Route path="/verify/:tokenPath" element={<VerifyEmailView />} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      <Route path="/share/:id" element={<ShareView />} />
      <Route path="/redeem/:token" element={<ShareRedeemView />} />
      <Route path="/embed/post/:id" element={<EmbedView />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/post/:id" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
    </Routes>
  )
}
